import { Card, CardContent } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import {
  Copy, Printer, Send, Lock, Loader2,
} from 'lucide-react';
import { toast } from 'sonner';

interface NoteActionBarProps {
  note: string;
  patientName: string;
  isSigned: boolean;
  canSign: boolean;
  /** True while the centralized sign mutation is in flight (pessimistic UI). */
  isSigning?: boolean;
  onFinalize: () => void;
  onSendToEhr?: () => void;
}

export function NoteActionBar({
  note,
  isSigned,
  canSign,
  isSigning = false,
  onFinalize,
  onSendToEhr,
}: NoteActionBarProps) {
  const handleCopy = () => {
    navigator.clipboard.writeText(note);
    toast.success('Note copied to clipboard');
  };

  const handlePrint = () => {
    window.print();
  };

  return (
    <Card data-clinical-print-ignore className="border-border/60 print:hidden">
      <CardContent className="p-4 space-y-3">
        <div className="flex flex-col sm:flex-row gap-2">
          <Button variant="outline" className="flex-1 h-14" onClick={handleCopy}>
            <Copy className="w-4 h-4 mr-2" /> Copy Note
          </Button>
          <Button variant="outline" className="flex-1 h-14" onClick={handlePrint}>
            <Printer className="w-4 h-4 mr-2" /> Print
          </Button>
          <Button
            variant="outline"
            className="flex-1 h-14"
            onClick={onSendToEhr ?? (() => toast.info('EHR sync not connected in demo mode.'))}
          >
            <Send className="w-4 h-4 mr-2" /> Send to Epic / EHR
          </Button>
        </div>

        <Button
          onClick={onFinalize}
          disabled={isSigned || !canSign || isSigning}
          className="w-full h-14 px-8 text-lg font-semibold bg-emerald-600 hover:bg-emerald-700 text-primary-foreground rounded-lg shadow-lg disabled:bg-muted disabled:text-muted-foreground disabled:opacity-50"
          size="lg"
        >
          {isSigned ? (
            <><Lock className="w-5 h-5 mr-2" />Signed & Locked</>
          ) : isSigning ? (
            <><Loader2 className="w-5 h-5 mr-2 animate-spin" />Signing & Recording Audit…</>
          ) : !canSign ? (
            <><Lock className="w-5 h-5 mr-2" />Blocked — Resolve Documentation Gaps</>
          ) : (
            <><Send className="w-5 h-5 mr-2" />Sign & Route to EHR</>
          )}
        </Button>
      </CardContent>
    </Card>
  );
}
